/**
 * Juthoor equivalents of the shared Overview/Timeline/Location/Participants/Settings
 * PageSections used by the Walk/generic initiative detail page — kept in one module
 * since they're all small, Juthoor-only facets of the same detail view.
 * @module components/initiatives/juthoor-detail-sections
 */

import { useState } from 'react';
import { Wallet, Sprout, TreePine, Users } from 'lucide-react';
import { t } from '@/lib/i18n/messages';
import { formatNumber } from '@/lib/utils/format';
import { useJuthoorLands } from '@/hooks/use-juthoor-lands';
import { useJuthoorParticipants } from '@/hooks/use-juthoor-participants';
import { JuthoorLandPolygonMap } from './juthoor-land-polygon-map';
import type { Locale } from '@/types/auth';
import type { JuthoorInitiative, JuthoorInitiativeStats } from '@/types/juthoor';

// ─── Status banners (rejection reason / budget-required notice) ───────────────

export function JuthoorStatusBanners({
  initiative,
  locale,
}: {
  initiative: JuthoorInitiative;
  locale: Locale;
}) {
  const isRTL = locale === 'ar';
  const isRejected = initiative.status === 'municipality_rejected';
  const isDraft = initiative.status === 'draft';
  const isEditable = isDraft || isRejected;
  const hasBudget = initiative.juthoor.budget != null && initiative.juthoor.budget > 0;

  if (!isRejected && !(isEditable && !hasBudget)) return null;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 pt-8 space-y-3">
      {isRejected && initiative.juthoor.municipality_rejection_reason && (
        <div className="rounded-2xl border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 px-4 py-3 flex items-start gap-3">
          <span className="material-icons-outlined text-red-500 text-lg mt-0.5">error_outline</span>
          <div className="text-sm">
            <p className="font-semibold text-red-700 dark:text-red-400 mb-0.5">
              {t(locale, 'rejectionReason')}
            </p>
            <p className="text-red-600 dark:text-red-300">
              {initiative.juthoor.municipality_rejection_reason}
            </p>
          </div>
        </div>
      )}
      {isEditable && !hasBudget && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800/60 px-4 py-3 flex items-start gap-3">
          <span className="material-icons-outlined text-amber-600 dark:text-amber-400 text-lg mt-0.5">info</span>
          <div className="text-sm text-amber-800 dark:text-amber-300">
            <p className="font-semibold mb-0.5">{isRTL ? 'الميزانية مطلوبة' : 'Budget required'}</p>
            <p>
              {isRTL
                ? 'يجب تعيين ميزانية المبادرة قبل إرسالها إلى الأمانة للموافقة.'
                : 'You must set a budget before submitting this initiative to the municipality for approval.'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Overview KPI strip (mirrors OverviewKpiStrip in initiative-detail-page.tsx) ──

export function JuthoorOverviewKpiStrip({
  initiative,
  stats,
  locale,
}: {
  initiative: JuthoorInitiative;
  stats: JuthoorInitiativeStats | null;
  locale: Locale;
}) {
  const isRTL = locale === 'ar';
  const cells = [
    {
      caption: isRTL ? 'الميزانية' : 'BUDGET',
      value: `${formatNumber(initiative.juthoor.budget ?? 0, locale)} NC`,
      icon: Wallet,
    },
    {
      caption: isRTL ? 'الأشجار المحجوزة' : 'TREES RESERVED',
      value: formatNumber(stats?.trees_reserved ?? 0, locale),
      icon: Sprout,
    },
    {
      caption: isRTL ? 'الأشجار المزروعة' : 'TREES PLANTED',
      value: formatNumber(stats?.trees_planted ?? 0, locale),
      icon: TreePine,
    },
    {
      caption: isRTL ? 'المشاركون' : 'PARTICIPANTS',
      value: formatNumber(stats?.total_participants ?? 0, locale),
      icon: Users,
    },
  ];

  return (
    <div className="rounded-2xl border border-border bg-white shadow-sm overflow-hidden">
      <div className="grid grid-cols-2 lg:grid-cols-4 divide-y lg:divide-y-0 lg:divide-x rtl:lg:divide-x-reverse divide-border">
        {cells.map((c) => (
          <div key={c.caption} className="p-5 md:p-6 flex flex-col gap-3 min-h-[112px]">
            <div className="flex items-center justify-between">
              <span className="font-sans text-[10px] tracking-[0.25em] text-foreground-secondary uppercase">
                {c.caption}
              </span>
              <span
                className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-brand-green/10 text-brand-green"
                aria-hidden
              >
                <c.icon className="w-3.5 h-3.5" strokeWidth={1.8} />
              </span>
            </div>
            <p className="font-display font-bold text-2xl md:text-3xl tracking-tight tabular-nums leading-none text-foreground">
              {c.value}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Timeline / analytics (ecosystem flow + planting progress) ────────────────

export function JuthoorAnalyticsPanel({
  stats,
  loading,
  locale,
}: {
  stats: JuthoorInitiativeStats | null;
  loading: boolean;
  locale: Locale;
}) {
  const isRTL = locale === 'ar';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-green" />
      </div>
    );
  }

  if (!stats) return null;

  const progressPct = Math.min(100, Math.round(stats.progress_percentage));

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-5">
          {t(locale, 'ecosystemFlow')}
        </h4>
        <div className={`flex items-center justify-between gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className="flex flex-col items-center gap-1 flex-1 text-center">
            <div className="w-12 h-12 rounded-full bg-brand-green/10 flex items-center justify-center">
              <span className="material-icons-outlined text-brand-green">how_to_reg</span>
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t(locale, 'treesReserved')}</span>
            <span className="text-lg font-bold text-gray-900 dark:text-white">
              {stats.trees_reserved.toLocaleString()}
            </span>
          </div>
          <span className="text-gray-300 dark:text-gray-600 text-xl font-light flex-shrink-0">→</span>
          <div className="flex flex-col items-center gap-1 flex-1 text-center">
            <div className="w-12 h-12 rounded-full bg-brand-green/10 flex items-center justify-center">
              <span className="material-icons-outlined text-brand-green">park</span>
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t(locale, 'treesPlanted')}</span>
            <span className="text-lg font-bold text-gray-900 dark:text-white">
              {stats.trees_planted.toLocaleString()}
            </span>
          </div>
          <span className="text-gray-300 dark:text-gray-600 text-xl font-light flex-shrink-0">→</span>
          <div className="flex flex-col items-center gap-1 flex-1 text-center">
            <div className="w-12 h-12 rounded-full bg-brand-green/10 flex items-center justify-center">
              <span className="material-icons-outlined text-brand-green">redeem</span>
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t(locale, 'treesRewarded')}</span>
            <span className="text-lg font-bold text-gray-900 dark:text-white">
              {stats.trees_rewarded.toLocaleString()}
            </span>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {t(locale, 'plantingProgress')}
          </span>
          <span className="text-sm font-bold text-brand-green">{progressPct}%</span>
        </div>
        <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className="h-full bg-brand-green rounded-full transition-all duration-500"
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          {stats.trees_planted.toLocaleString()} / {stats.total_trees.toLocaleString()}
        </p>
      </div>
    </div>
  );
}

// ─── Location (active land selection + polygon map) ───────────────────────────

export function JuthoorLocationSection({
  initiative,
  locale,
}: {
  initiative: JuthoorInitiative;
  locale: Locale;
}) {
  const isRTL = locale === 'ar';
  const { data: lands, loading } = useJuthoorLands();

  if (loading) {
    return (
      <div className="space-y-2">
        <div className="h-4 w-3/4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        <div className="h-3 w-1/2 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        <div className="h-40 bg-gray-100 dark:bg-gray-700 rounded-xl animate-pulse mt-3" />
      </div>
    );
  }

  const land = lands.find((l) => l.landId === initiative.juthoor.netzero_land_id) ?? null;
  const displayName = initiative.juthoor.land_name ?? land?.landName ?? null;
  const polygon = initiative.juthoor.land_polygon ?? land?.polygon ?? null;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-100 dark:border-gray-700 space-y-3">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-brand-green/10 flex items-center justify-center flex-shrink-0">
          <span className="material-icons-outlined text-brand-green">landscape</span>
        </div>
        <div>
          <p className="font-semibold text-gray-900 dark:text-white text-sm">
            {displayName ?? (isRTL ? `رقم الأرض: ${initiative.juthoor.netzero_land_id}` : `Land ID: ${initiative.juthoor.netzero_land_id}`)}
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
            ID: {initiative.juthoor.netzero_land_id}
          </p>
        </div>
      </div>
      {polygon?.points && polygon.points.length >= 3 && (
        <JuthoorLandPolygonMap points={polygon.points} landName={displayName ?? undefined} height={320} />
      )}
    </div>
  );
}

// ─── Participants (participation ledger) ───────────────────────────────────────

export function JuthoorParticipantsSection({
  initiativeId,
  locale,
}: {
  initiativeId: string;
  locale: Locale;
}) {
  const isRTL = locale === 'ar';
  const [page, setPage] = useState(1);
  const { data: participants, meta, loading } = useJuthoorParticipants(initiativeId, page);

  if (loading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-10 bg-gray-100 dark:bg-gray-700 rounded animate-pulse" />
        ))}
      </div>
    );
  }

  if (participants.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400 py-6 text-center">
        {isRTL ? 'لا يوجد مشاركون بعد' : 'No participants yet'}
      </p>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 dark:border-gray-700">
              <th className="pb-3 font-medium text-gray-500 dark:text-gray-400 text-start">
                {isRTL ? 'المشارك' : 'Participant'}
              </th>
              <th className="pb-3 font-medium text-gray-500 dark:text-gray-400 text-start">
                {t(locale, 'joinedDate')}
              </th>
              <th className="pb-3 font-medium text-gray-500 dark:text-gray-400 text-end">
                {t(locale, 'treesReserved')}
              </th>
              <th className="pb-3 font-medium text-gray-500 dark:text-gray-400 text-end">
                {t(locale, 'treesPlanted')}
              </th>
              <th className="pb-3 font-medium text-gray-500 dark:text-gray-400 text-end">
                {t(locale, 'rewardsEarned')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
            {participants.map((entry) => (
              <tr key={entry.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                <td className="py-3 font-medium text-gray-900 dark:text-white">{entry.participant_name}</td>
                <td className="py-3 text-gray-500 dark:text-gray-400">{entry.joined_at}</td>
                <td className="py-3 text-end text-gray-700 dark:text-gray-300">
                  {entry.trees_reserved.toLocaleString()}
                </td>
                <td className="py-3 text-end text-gray-700 dark:text-gray-300">
                  {entry.trees_planted.toLocaleString()}
                </td>
                <td className="py-3 text-end font-medium text-brand-green">
                  {entry.rewards_earned.toLocaleString()} GP
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {meta && meta.last_page > 1 && (
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            {isRTL ? 'السابق' : 'Previous'}
          </button>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {page} / {meta.last_page}
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(meta.last_page, p + 1))}
            disabled={page >= meta.last_page}
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            {isRTL ? 'التالي' : 'Next'}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Settings & details (configuration summary + sustainable impact) ──────────

export function JuthoorSettingsInfo({
  initiative,
  stats,
  locale,
}: {
  initiative: JuthoorInitiative;
  stats: JuthoorInitiativeStats | null;
  locale: Locale;
}) {
  const isRTL = locale === 'ar';
  const treesPlanted = stats?.trees_planted ?? 0;
  const co2Kg = Math.round(treesPlanted * 21);
  const o2Kg = Math.round(treesPlanted * 118);

  const infoCards = [
    {
      icon: 'category',
      label: t(locale, 'initiativeType'),
      value: t(locale, 'typeJuthoor'),
    },
    {
      icon: 'forest',
      label: t(locale, 'totalTrees'),
      value: String(initiative.juthoor.total_trees),
    },
    {
      icon: 'redeem',
      label: t(locale, 'rewardPerTree'),
      value: `${initiative.juthoor.reward_per_tree} GP`,
    },
    {
      icon: 'groups',
      label: t(locale, 'maxTreesPerClient'),
      value: String(initiative.juthoor.max_trees_per_client),
    },
    {
      icon: 'calendar_today',
      label: t(locale, 'startDate'),
      value: initiative.juthoor.start_date,
    },
    ...(initiative.juthoor.planting_date
      ? [
          {
            icon: 'eco',
            label: isRTL ? 'تاريخ الزراعة' : 'Planting Date',
            value: initiative.juthoor.planting_date,
          },
        ]
      : []),
  ];

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {infoCards.map((card) => (
          <div
            key={card.label}
            className="bg-card p-6 rounded-lg shadow-sm border border-border flex items-center gap-4"
          >
            <div className="w-12 h-12 bg-brand-green/10 text-brand-green rounded-xl flex items-center justify-center flex-shrink-0">
              <span className="material-icons-outlined">{card.icon}</span>
            </div>
            <div>
              <p className={`text-xs text-muted-foreground font-medium mb-1 uppercase tracking-wider ${isRTL ? 'text-right' : ''}`}>
                {card.label}
              </p>
              <p className={`text-base font-bold text-foreground ${isRTL ? 'text-right' : ''}`}>
                {card.value}
              </p>
            </div>
          </div>
        ))}
      </div>

      <section className="bg-card rounded-lg shadow-sm border border-border overflow-hidden">
        <div className="p-6 border-b border-border flex items-center gap-2 bg-slate-50/50/30">
          <span className="material-icons-outlined text-brand-green">co2</span>
          <h3 className="text-base font-bold text-foreground">
            {t(locale, 'sustainableImpact')}
          </h3>
        </div>
        <div className="p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="text-center p-6 bg-muted/50 rounded-lg border border-transparent hover:border-brand-green/20 transition-all">
              <p className={`text-sm text-foreground-secondary mb-3 ${isRTL ? 'text-right md:text-center' : ''}`}>
                {isRTL ? 'كغ ثاني أكسيد الكربون المُمتص / سنة' : 'kg CO₂ absorbed / year'}
              </p>
              <p className="text-xl font-bold text-brand-green">{co2Kg.toLocaleString()}</p>
            </div>
            <div className="text-center p-6 bg-muted/50 rounded-lg border border-transparent hover:border-brand-green/20 transition-all">
              <p className={`text-sm text-foreground-secondary mb-3 ${isRTL ? 'text-right md:text-center' : ''}`}>
                {isRTL ? 'كغ أكسجين مُنتج / سنة' : 'kg O₂ produced / year'}
              </p>
              <p className="text-xl font-bold text-brand-green">{o2Kg.toLocaleString()}</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-3 text-center">
            {isRTL
              ? `محسوب من ${treesPlanted.toLocaleString()} شجرة مزروعة — استناداً إلى معدلات الهيئة الحكومية الدولية المعنية بتغير المناخ`
              : `Based on ${treesPlanted.toLocaleString()} planted trees — IPCC average estimates`}
          </p>
        </div>
      </section>
    </div>
  );
}
